import paper from "paper";
import Parameter from "./parameter";
import Params from "./params";
import ConnectionTarget from "./connectionTarget";
import Device from "./device";
import * as FeatureRenderer2D from "../view/render2D/featureRenderer2D";
import Layer from "./layer";
import uuid from "node-uuid";
import { ConnectionInterchangeV1_2, ConnectionPathInterchangeV1_2, ConnectionTargetInterchangeV1 } from "./init";
import { Segment, Point } from "./init";
import ConnectionUtils from "../utils/connectionUtils";
import { clipSegmentsByValveGaps, ValveGapRect } from "../utils/valveChannelClip";
import { ComponentAPI } from "@/componentAPI";
import MapUtils from "../utils/mapUtils";
import ExportUtils from "../utils/exportUtils";
import Feature from "./feature";
import ComponentPort from "./componentPort";
import { DEFAULT_CHANNEL_WIDTH_UM } from "../library/channelWidths";

/**
 * This class contains the connection abstraction used in the interchange format and the
 * high level device model of the microfluidic.
 */
export default class Connection {
    protected _params: Params;
    protected _name: string;
    protected _id: string;
    protected _type: string;
    protected _entity: string;
    protected _featureIDs: Array<string>;
    protected _nodes: any;
    protected _bounds: paper.Rectangle | null;
    protected _source: ConnectionTarget | null;
    protected _sinks: Array<ConnectionTarget>;
    protected _paths: Array<Array<Point>>;
    protected _objects: any;
    protected _routed: boolean;
    protected _layer: Layer;

    features: Array<Feature> = [];

    /**
     * Default Connection Constructor
     * @param {String} type
     * @param {Params} params
     * @param {String} name
     * @param {String} mint
     * @param {String} id
     */
    constructor(type: string, params: Params, name: string, mint: string, layer: Layer, id: string = ComponentAPI.generateID()) {
        this._params = params;
        this._name = name;
        this._id = id;
        this._type = type;
        this._entity = mint;
        //This stores the features that are a part of the component
        this._featureIDs = [];
        this._nodes = [];
        //TODO: Need to figure out how to effectively search through these
        this._bounds = null;
        this._source = null;
        this._sinks = [];
        this._paths = [];
        this._objects = [];
        this._routed = false;
        this._layer = layer;
    }

    get layer(): Layer {
        return this._layer;
    }

    set layer(layer: Layer) {
        this._layer = layer;
    }
    /**
     * Gets the sinks in the connection
     * @returns {ConnectionTarget[]} Returns an array with the sinks
     * @memberof Connection
     */
    get sinks(): Array<ConnectionTarget> {
        return this._sinks;
    }

    /**
     * Gets the source of the connection
     * @returns {ConnectionTarget} Returns the source of the connection
     * @memberof Connection
     */
    get source(): ConnectionTarget | null {
        return this._source;
    }

    /**
     * Checks if the connection is routed
     * @returns {Boolean} Returns true whether if it is routed or not
     * @memberof Connection
     */
    get routed(): boolean {
        return this._routed;
    }

    /**
     * Sets if the connection is routed
     * @param {Boolean} value true if it's router or false if it's not
     * @returns {void}
     * @memberof Connection
     */
    set routed(value: boolean) {
        this._routed = value;
    }

    /**
     * Returns the list of features associated with the connection
     * @return {Feature[]}
     * @memberof Connection
     */
    get featureIDs(): Array<string> {
        return this._featureIDs;
    }

    /**
     * Returns the params associated with the connection
     * @return {Params}
     * @memberof Connection
     */
    get params(): Params {
        return this._params;
    }


    /**
     * Sets the bounds i.e. the x,y position and the width and length of the component
     * @param {paper.Rectangle} bounds PaperJS Rectangle object associated with a Path.bounds property
     * @memberof Connection
     * @returns {void}
     */
    setBounds(bounds: paper.Rectangle): void {
        this._bounds = bounds;
        let topleftpt = bounds.topLeft;
        this._params.updateParameter("position", [topleftpt.x, topleftpt.y]);
        this._params.updateParameter("xspan", bounds.width);
        this._params.updateParameter("yspan", bounds.height);
    }

    /**
     * Updates the parameters stored by the component
     * @param {String} key Identifier of the parameter
     * @param {any} value
     * @memberof Connection
     * @returns {void}
     */
    updateParameter(key: string, value: any): void {
        this._params.updateParameter(key, value);

        console.log("Key: ", key, " Val: ", value);

        if (key != "segments") {
            for (const featureidtochange of this._featureIDs) {
                // Get the feature and update only if that parameter exists on the feature.
                // Some legacy files can contain connection features with older template keys.
                const feature = ConnectionUtils.getFeatureFromID(featureidtochange);
                const params = feature.getParams();
                if (Object.prototype.hasOwnProperty.call(params, key)) {
                    feature.updateParameter(key, value);
                }
            }
        }

        // this._params[key] = value;
        // this.updateView();
    }

    /**
     * Generates the object that needs to be serialzed into JSON for interchange format V1
     * @returns {ConnectionInterchangeV1_2} Object
     * @memberof Connection
     */
    toInterchangeV1(): ConnectionInterchangeV1_2 {
        if (this._source === null) {
            throw new Error("Source is null, cannot convert connection to interchange format:" + this._id);
        }
        const outputpaths: Array<ConnectionPathInterchangeV1_2> = [];
        for (let i = 0; i < this._paths.length; i++) {
            const path = this._paths[i];
            const outputpath: ConnectionPathInterchangeV1_2 = {
                source: this._source !== null ? this._source.toJSON() : null,
                sink: this.sinks.length > i ? this._sinks[i].toJSON() : null,
                wayPoints: path,
                // Get the id's of the features
                features: this.features.map(feature => {return feature.id;})
            };
            outputpaths.push(outputpath);
        }
        const output: ConnectionInterchangeV1_2 = {
            id: this._id,
            name: this._name,
            entity: this._entity,
            source: this._source.toJSON(),
            sinks: [],
            paths: outputpaths,
            params: this._params.toJSON(),
            layer: this._layer.id
        };

        if (this._source !== null) {
            output.source = this._source.toJSON();
        }
        if (this._sinks !== null && this._sinks.length > 0) {
            let sinks = [];
            for (const sink of this._sinks) {
                sinks.push(sink.toJSON());
            }
            output.sinks = this._sinks.map(sink => {
                return ExportUtils.toConnectionTargetInterchangeV1(sink);
            });
        }
        return output;
    }

    /**
     * Returns the ID of the component
     * @returns {String}
     * @memberof Connection
     */
    get id(): string {
        return this._id;
    }

    /**
     * Allows the user to set the name of the component
     * @param {String} name Name of the component
     * @memberof Connection
     * @returns {void}
     */
    set name(name: string) {
        this._name = name;
    }

    /**
     * Returns the name of the component
     * @returns {String} Name of the component
     * @memberof Connection
     */
    get name(): string {
        return this._name;
    }

    /**
     * Gets the 3DuF Type of the component, this will soon be depreciated and merged with
     * the MINT references
     * @returns {String}
     * @memberof Connection
     */
    get type(): string {
        return this._type;
    }

    /**
     * Returns the position of the component
     * @return {string}
     * @memberof Connection
     */
    /**
     * Returns the value of the parameter stored against the following key in teh component params
     * @returns {any}
     * @memberof Connection
     */
    getValue(key: string): any {
        try {
            return this._params.getValue(key);
        } catch (err) {
            throw new Error("Unable to get value for key: " + key);
        }
    }

    /**
     * Adds a feature that is associated with the component
     * @param {String} featureID String id of the feature
     * @memberof Connection
     * @returns {void}
     */
    addFeatureID(featureID: string): void {
        this._featureIDs.push(featureID);
        //Now update bounds
        // this.__updateBounds();
    }

    /**
     * This method updates the bounds of the component
     * @memberof Connection
     * @returns {void}
     * @protected
     */
    protected updateBounds(): void {
        console.log("test");
        let bounds = null;
        let feature = null;
        let renderedfeature = null;
        for (const featureid of this._featureIDs) {
            // gets teh feature defined by the id
            feature = ConnectionUtils.getFeatureFromID(featureid);
            console.log(feature);
            renderedfeature = FeatureRenderer2D.renderFeature(feature, null);
            console.log("rendered:");
            console.log(renderedfeature);
            if (bounds === null) {
                bounds = renderedfeature.bounds;
            } else {
                bounds = bounds.unite(renderedfeature.bounds);
            }
        }
        this._bounds = bounds;
    }

    /**
     * Sets the params associated with the component
     * @param {Params} params key -> Parameter Set
     * @returns {void}
     * @memberof Connection
     */
    set params(params: Params) {
        this._params = params;
        //TODO: Modify all the associated Features
        for (let key in params) {
            let value = params.getValue(key);
            for (const featureidtochange of this._featureIDs) {

                //Get the feature id and modify it
                let feature = ConnectionUtils.getFeatureFromID(featureidtochange);
                feature.updateParameter(key, value.getValue());
            }
        }
    }

    /**
     * Returns the list of waypoints associated with the connection
     * @return {Point[]}
     * @memberof Connection
     */
    getPaths(): Array<Array<Point>> {
        return this._paths;
    }

    /**
     * Updates the segments of the connection
     * @param {Segment[]} segments
     * @memberof Connection
     * @returns {void}
     */
    updateSegments(segments: Array<Segment>): void {
        this.updateParameter("segments", new Parameter("SegmentArray", segments));
        for (const featureidtochange of this._featureIDs) {

            const feature = ConnectionUtils.getFeatureFromID(featureidtochange);
            // feature.updateParameter('position', center);
            feature.updateParameter("segments", segments);
        }
    }

    /**
     * Inserts the gap using the boundingbox
     * @param {any} boundingbox
     * @memberof Connection
     * @returns {boolean}
     */
    insertFeatureGap(gapGeometry: paper.Rectangle | paper.PathItem): boolean {
        let foundflag = false;
        let gapPath: paper.PathItem;
        let removeGapPath = false;
        if (gapGeometry instanceof paper.Rectangle) {
            gapPath = new paper.Path.Rectangle(gapGeometry);
            removeGapPath = true;
        } else {
            gapPath = gapGeometry;
        }
        // Check which segment I need to break
        const segments = this.getValue("segments");
        for (const i in segments) {
            const segment = segments[i];
            const line = new paper.Path.Line(new paper.Point(segment[0]), new paper.Point(segment[1]));
            const intersections = line.getIntersections(gapPath);
            // console.log("Intersections found", intersections);
            if (intersections.length >= 2) {
                const sortedIntersections = intersections
                    .map(intersection => intersection.point)
                    .sort((a, b) => {
                        const startPoint = line.firstSegment.point;
                        return startPoint.getDistance(a) - startPoint.getDistance(b);
                    });
                const break1 = sortedIntersections[0];
                const break2 = sortedIntersections[sortedIntersections.length - 1];
                const newsegs = this.breakSegment(segment, break1, break2);
                console.log("breaking:", segment, newsegs);
                if (newsegs.length !== 2) {
                    throw new Error("Could not break the segments correctly");
                }
                segments.splice(i, 1, newsegs[0], newsegs[1]);
                foundflag = true;
            } else if (intersections.length === 1) {
                console.error("Only found 1 intersection point so going to use a different method");
                console.log("Found Intersection:", intersections);

                console.log("Segments:", segments);
                console.log("line:", line);
                console.log("Gap geometry:", gapPath);
            } else {
                console.error("No intersections found so going to use a different method");
                console.log("Found Intersection:", intersections);

                console.log("Segments:", segments);
                console.log("line:", line);
                console.log("Gap geometry:", gapPath);
            }
            line.remove();
        }

        // Now that we exit the check for every segment we can verify if this is ok
        if (!foundflag) {
            console.error("There's something funky going on with the intersection,no intersections found");
            console.log("Segments:", segments);
            // console.log("line:", line);
            console.log("Gap geometry:", gapPath);
            if (removeGapPath) {
                gapPath.remove();
            }
            throw new Error("Could not find 2 intersection points, hence aborting the whole thing");
        }
        // console.log("raw new segments:", segments);
        this.updateSegments(segments);
        if (removeGapPath) {
            gapPath.remove();
        }

        return foundflag;
    }

    /**
     * Split connection centerlines at VALVE3D FLOW gaps without paper.js
     * boolean / intersection tests (those fail or get skipped on import).
     */
    insertValveGapsGeometric(gaps: ValveGapRect[]): boolean {
        if (!Array.isArray(gaps) || gaps.length === 0) {
            return false;
        }
        const segments = this.getValue("segments");
        if (!Array.isArray(segments) || segments.length === 0) {
            return false;
        }
        const clipped = clipSegmentsByValveGaps(segments, gaps);
        this.updateSegments(clipped);
        return clipped.length !== segments.length;
    }

    /**
     * Breaks the segment at the 2 points given by the points
     * @param {Point[]} segment
     * @param {paper.Point} break1
     * @param {paper.Point} break2
     * @return {Segment[]} Returns the two segments
     * @memberof Connection
     * @protected
     */
    protected breakSegment(segment: Segment, break1: paper.Point, break2: paper.Point): Array<Segment> {
        //Generate 2 segments from this 1 segemnt
        const p1 = new paper.Point(segment[0]);
        const p2 = new paper.Point(segment[1]);

        let segment1: Segment, segment2: Segment;
        const p1_break1 = p1.getDistance(break1);
        const p2_break1 = p2.getDistance(break1);
        const p1_break2 = p1.getDistance(break2);
        const p2_break2 = p2.getDistance(break2);

        // Find out if break1 is closer to p1 or p2
        if (p1_break1 + p2_break2 < p2_break1 + p1_break2) {
            // break1 is closer to p1 and break2 is closer to p2\
            segment1 = [
                [Math.round(p1.x), Math.round(p1.y)],
                [Math.round(break1.x), Math.round(break1.y)]
            ];
            segment2 = [
                [Math.round(p2.x), Math.round(p2.y)],
                [Math.round(break2.x), Math.round(break2.y)]
            ];
        } else {
            // break1 is closer to p2 and break1 is closer to p1
            segment1 = [
                [Math.round(p2.x), Math.round(p2.y)],
                [Math.round(break1.x), Math.round(break1.y)]
            ];
            segment2 = [
                [Math.round(p1.x), Math.round(p1.y)],
                [Math.round(break2.x), Math.round(break2.y)]
            ];
        }
        return [segment1, segment2];
    }

    /**
     * This method is used to import the component from Interchange V1 JSON
     * @param json
     * @returns {Connection} Returns a connection object
     * @memberof Connection
     */
    static fromInterchangeV1(device: Device, json: ConnectionInterchangeV1_2): Connection {
        // let set;
        // if (json.hasOwnProperty("set")) set = json.set;
        // else set = "Basic";
        // //TODO: This will have to change soon when the thing is updated
        // throw new Error("Need to implement Interchange V1 Import for component object");
        // //return Device.makeFeature(json.macro, set, json.params, json.name, json.id, json.type);

        const name = json.name;
        const id = json.id;
        const entity = json.entity;
        const params = json.params;
        const layer = device.getLayer(json.layer);
        if (layer === null) {
            throw new Error("Could not find layer with id: " + json.layer);
        }

        // Check if the params have the other unique elements necessary otherwise add them as null
        if (!Object.prototype.hasOwnProperty.call(params, "start")) {
            // Setting this value to origin
            params.start = [0, 0];
        }
        if (!Object.prototype.hasOwnProperty.call(params, "end")) {
            // Setting this value to origin
            params.end = [0, 0];
        }
        if (!Object.prototype.hasOwnProperty.call(params, "wayPoints")) {
            // TODO: setting a single waypoint at origin
            params.wayPoints = [
                [0, 0],
                [1, 2]
            ];
        }
        if (!Object.prototype.hasOwnProperty.call(params, "segments")) {
            // TODO: Setting a default segment from origin to origin
            params.segments = [
                [
                    [0, 0],
                    [0, 0]
                ],
                [
                    [0, 0],
                    [0, 0]
                ]
            ];
        }
        let definition = ComponentAPI.getDefinition("Connection");
        if (definition === null || definition === undefined) {
            throw new Error("Could not find the definition for the Connection");
        }
        const paramstoadd = new Params(params, MapUtils.toMap(definition.unique), MapUtils.toMap(definition.heritable));

        const connection = new Connection(entity, paramstoadd, name, entity, layer, id);
        if (Object.prototype.hasOwnProperty.call(json, "source")) {
            if (json.source !== null && json.source !== undefined) {
                connection.setSourceFromJSON(device, json.source);
            }
        }
        if (Object.prototype.hasOwnProperty.call(json, "sinks")) {
            if (json.sinks !== null && json.sinks !== undefined) {
                for (const i in json.sinks) {
                    const sink = json.sinks[i];
                    connection.addSinkFromJSON(device, sink);
                }
            }
        }
        if (Object.prototype.hasOwnProperty.call(json, "paths")) {
            if (json.paths !== null && json.paths !== undefined) {
                for (const i in json.paths) {
                    connection.addWayPoints(json.paths[i].wayPoints);
                }
            }
        }

        return connection;
    }

    /**
     * Goes through teh waypoints and generates the connection segments
     * @return {SegmentArray}
     * @memberof Connection
     */
    regenerateSegments(): void {
        const pathscopy = this.getPaths();
        const ret: Array<Segment> = [];
        let waypointscopy;
        for (const j in pathscopy) {
            waypointscopy = pathscopy[j];
            for (let i = 0; i < waypointscopy.length - 1; i++) {
                const segment: Segment = [waypointscopy[i], waypointscopy[i + 1]];
                ret.push(segment);
            }
        }
        this.updateSegments(ret);
    }

    // /**
    //  * Allows the user to set the source of the connection
    //  * @param {Object} component
    //  * @param {ComponentPort} port
    //  * @memberof Connection
    //  * @returns {void}
    //  */
    // setSource(component: string, port: ComponentPort) {
    //     if (typeof component != "string" && !(component instanceof String)) {
    //         console.error("The reference object value can only be a string");
    //     }
    //     this._source = new ConnectionTarget(component, port);
    // }

    // /**
    //  * Allows the user to add a sink to the connection
    //  * @param {string} component
    //  * @param {ComponentPort} port
    //  * @memberof Connection
    //  * @returns {void}
    //  */
    // addSink(component: string, port: ComponentPort) {
    //     if (typeof component != "string" || !(component instanceof String)) {
    //         console.error("The reference object value can only be a string");
    //     }
    //     this._sinks.push(new ConnectionTarget(component, port));
    // }

    /**
     * Adds a new connection target to either the source or the sinks of the connection object. Requires the user to pass
     * a ConnectionTarget Object or else it will throw an error.
     * @param {string} connectiontarget
     * @memberof Connection
     * @returns {void}
     */
    addConnectionTarget(connectiontarget: ConnectionTarget): void {
        if (!(connectiontarget instanceof ConnectionTarget) || connectiontarget === null || connectiontarget == undefined) {
            console.error("Cannot add non-ConnectionTarget object as source or sink");
        }

        if (this._source === null) {
            this._source = connectiontarget;
        } else {
            //TODO: Check for duplicates - does it matter actually ?
            this._sinks.push(connectiontarget);
        }
    }

    /**
     * Tries to delete any connection target reference that uses the said component
     * @param {string} componentid Component ID
     * @return {boolean} Returns true if any corresponding connection target is found
     * @memberof Connection
     *
     */
    tryDeleteConnectionTarget(componentid: string): boolean {
        let ret = false;

        let source = this._source;
        if (source !== null) {
            if (source.component.id == componentid) {
                //Remove the source object
                this._source = null;
                ret = true;
            }
        }

        for (let i in this._sinks) {
            let sink = this._sinks[i];

            if (sink.component.id == componentid) {
                this._sinks.splice(+i, 1);
                ret = true;
            }
        }

        return ret;
    }

    /**
     * Adds a new set of waypoints to the path field of the connection
     * @param {} wayPoints
     * @memberof Connection
     * @returns {void}
     */
    addWayPoints(wayPoints: Array<Point>): void {
        this._paths.push(wayPoints);
    }

    /**
     * After JSON load, rewrite terminal waypoints onto library port positions
     * (inset into the body) so channels meet the primitive that source/sink
     * name, even when Parchmint ports were AABB-relative.
     */
    snapImportedTerminals(): boolean {
        if (this._source === null && this._sinks.length === 0) {
            return false;
        }
        let width = DEFAULT_CHANNEL_WIDTH_UM;
        try {
            width = Number(this.getValue("channelWidth") || DEFAULT_CHANNEL_WIDTH_UM);
        } catch {
            try {
                width = Number(this.getValue("width") || DEFAULT_CHANNEL_WIDTH_UM);
            } catch {
                width = DEFAULT_CHANNEL_WIDTH_UM;
            }
        }
        const overlap = Math.max(width / 2, 16);
        const snapEnd = (target: ConnectionTarget | null | undefined, fallback: Point): Point => {
            if (!target) {
                return fallback;
            }
            const port = target.component.ports.get(String(target.portLabel));
            if (!port) {
                return fallback;
            }
            const abs = ComponentPort.calculateAbsolutePosition(port, target.component);
            const entity = String(
                (target.component as any).mint ||
                    (target.component as any)._entity ||
                    ""
            ).toUpperCase();
            if (entity === "MIXER" || entity === "CURVED MIXER" || entity === "MIXER3D") {
                return ComponentPort.mixerTerminalHinge(abs, target.component, width);
            }
            return ComponentPort.insetTowardCenter(abs, target.component, overlap);
        };
        if (this._paths.length === 0) {
            try {
                const wp = this.getValue("wayPoints");
                if (Array.isArray(wp) && wp.length >= 2) {
                    this._paths.push(wp.map((p: Point) => [p[0], p[1]] as Point));
                }
            } catch {
                // no stored waypoints
            }
        }
        let changed = false;
        for (const path of this._paths) {
            if (!path || path.length < 2) {
                continue;
            }
            path[0] = snapEnd(this._source, path[0]);
            path[path.length - 1] = snapEnd(this._sinks[0], path[path.length - 1]);
            changed = true;
        }
        if (!changed) {
            return false;
        }
        this.regenerateSegments();
        const first = this._paths[0];
        if (first && first.length >= 2) {
            this.updateParameter("wayPoints", first);
            this.updateParameter("start", first[0]);
            this.updateParameter("end", first[first.length - 1]);
        }
        return true;
    }

    /**
     * Merges connections
     * @param {Connection} connection
     * @memberof Connection
     * @returns {void}
     */
    mergeConnection(connection: Connection): void {
        console.error("Merge the newly found connection with the new connection");
        // TODO:
        /*
        1. Transfer all the paths
        2. Transfer all the ConnectionTargets
        3. Transfer all the other objects
        4. Move the params
        5.
         */
    }

    /**
     * Converts from JSON format to connection object
     * @param {Object} device
     * @param {JSON} json
     * @memberof Connection
     * @returns {void}
     */
    setSourceFromJSON(device: Device, json: ConnectionTargetInterchangeV1): void {
        const target = ConnectionTarget.fromJSON(device, json);
        this._source = target;
    }

    /**
     * Adds a sink to the connection
     * @param {Object} device
     * @param {JSON} json
     * @memberof Connection
     * @returns {void}
     */
    addSinkFromJSON(device: Device, json: ConnectionTargetInterchangeV1): void {
        let target = ConnectionTarget.fromJSON(device, json);
        this._sinks.push(target);
    }

    /**
     * Adds a feature to the connection
     *
     * @param {Feature} feature
     * @memberof Connection
     */
    addFeature(feature: Feature): void {
        this.features.push(feature);
    }

    /**
     * Removes a feature from the connection
     *
     * @param {Feature} feature
     * @memberof Connection
     */
    removeFeature(feature: Feature): void {
        this.features.splice(this.features.indexOf(feature), 1);
    }

}
